import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Eye, EyeSlash, Key, Lock, QrCode, SignIn, SpinnerGap } from '@phosphor-icons/react';
import { adminInternsAPI } from '../../services/api';

const statusTone = (status) => {
  if (status === 'completed') return 'bg-emerald-50 text-emerald-700';
  if (status === 'submitted') return 'bg-amber-50 text-amber-700';
  if (status === 'closed') return 'bg-gray-100 text-gray-500';
  return 'bg-sky-50 text-sky-700';
};

const pretty = (iso) => {
  if (!iso) return '—';
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const emptyScan = (intern, periodEnd) => ({
  name: intern.name,
  college: intern.college,
  phone: intern.phone,
  email: intern.email,
  intern_code: intern.intern_code,
  role_title: intern.role_title,
  join_date: intern.started_at,
  complete_date: periodEnd,
  issued_at: todayISO()
});

const AdminInternDetail = () => {
  const { internId } = useParams();
  const navigate = useNavigate();
  const [intern, setIntern] = useState(null);
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [openWeek, setOpenWeek] = useState(null);
  const [briefs, setBriefs] = useState({});
  const [scan, setScan] = useState({});
  const [pw, setPw] = useState({ password: '', confirm: '', admin_password: '' });
  const [showPw, setShowPw] = useState({});

  const load = async () => {
    const { data } = await adminInternsAPI.show(internId);
    const next = data.intern;
    setIntern(next);
    const map = {};
    (next.tasks || []).forEach((task) => {
      map[task.id] = { title: task.title || '', description: task.description || '' };
    });
    setBriefs(map);
    const periodEnd = next.planned_end || next.completed_at;
    setScan((prev) => (prev.intern_code === next.intern_code ? prev : emptyScan(next, periodEnd)));
  };

  useEffect(() => {
    document.title = 'Intern | Growmore admin';
    load().catch(() => setNotice('Could not load intern.'));
  }, [internId]);

  const act = async (fn, ok) => {
    setBusy(true);
    try {
      const { data } = await fn();
      setNotice(ok || data.message || 'Saved.');
      await load();
      return data;
    } catch (err) {
      setNotice(err.response?.data?.message || 'Action failed.');
      return null;
    } finally {
      setBusy(false);
    }
  };

  const loginAs = async () => {
    const data = await act(() => adminInternsAPI.impersonate(intern.id), `Opening ${intern.name} portal…`);
    if (!data?.token) return;
    localStorage.setItem('intern_token', data.token);
    window.open('/intern/portal', '_blank', 'noopener');
  };

  const changePassword = async (event) => {
    event.preventDefault();
    if (pw.password !== pw.confirm) {
      setNotice('New password and confirm password do not match.');
      return;
    }
    const data = await act(
      () => adminInternsAPI.setPassword(intern.id, {
        password: pw.password,
        admin_password: pw.admin_password
      }),
      'Intern password updated.'
    );
    if (data) setPw({ password: '', confirm: '', admin_password: '' });
  };

  if (!intern) {
    return <p className="text-gray-500 flex items-center gap-2"><SpinnerGap className="w-5 h-5 animate-spin" /> Loading intern…</p>;
  }

  const tasks = intern.tasks || [];
  const lastWeekNo = intern.weeks_total || 1;
  const last = tasks.find((item) => Number(item.week_number) === Number(lastWeekNo));
  const offer = (intern.letters || []).find((item) => item.type === 'offer');
  const completion = (intern.letters || []).find((item) => item.type === 'completion');
  const periodEnd = intern.planned_end || intern.completed_at;
  const issueToday = todayISO();
  const currentWeek = Number(intern.current_week || 1);
  const allApproved = Array.from({ length: lastWeekNo }, (_, i) => i + 1).every(
    (week) => tasks.find((task) => Number(task.week_number) === week)?.review_status === 'reviewed'
  );

  const issueCompletion = async () => {
    const saved = await act(
      () => adminInternsAPI.updateIntern(intern.id, {
        name: scan.name,
        college: scan.college,
        phone: scan.phone,
        email: scan.email,
        sync_letters: true
      }),
      'Details saved.'
    );
    if (!saved) return;
    const data = await act(
      () => adminInternsAPI.issueLetter(intern.id, {
        type: 'completion',
        join_date: intern.started_at,
        complete_date: periodEnd,
        issued_at: scan.issued_at || issueToday
      })
    );
    return data;
  };

  const field = (key, placeholder, type = 'password') => (
    <div className="relative">
      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      <input
        type={showPw[key] ? 'text' : type}
        className="input-field !pl-10 !pr-11 text-sm"
        placeholder={placeholder}
        value={pw[key]}
        onChange={(e) => setPw((p) => ({ ...p, [key]: e.target.value }))}
        autoComplete={key === 'admin_password' ? 'current-password' : 'new-password'}
        minLength={key === 'admin_password' ? undefined : 8}
        required
      />
      <button
        type="button"
        onClick={() => setShowPw((p) => ({ ...p, [key]: !p[key] }))}
        className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-gray-400"
        aria-label="Show password"
      >
        {showPw[key] ? <EyeSlash className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );

  return (
    <div className="space-y-4 sm:space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button type="button" onClick={() => navigate('/admin/interns')} className="inline-flex items-center gap-2 text-sm font-semibold text-gray-600">
          <ArrowLeft className="w-4 h-4" /> Interns
        </button>
        <span className={`text-xs font-semibold px-3 py-1.5 rounded-full capitalize ${statusTone(intern.status)}`}>{intern.status}</span>
      </div>

      {notice && <p className="text-sm bg-brand-50 text-brand-800 border border-brand-100 rounded-2xl px-4 py-3">{notice}</p>}

      <section className="rounded-2xl sm:rounded-3xl bg-white border border-gray-100 p-4 sm:p-5 shadow-soft">
        <p className="text-xs font-bold uppercase text-brand-600">{intern.role_title}</p>
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-gray-900">{intern.name}</h1>
        <p className="text-sm text-gray-500 mt-1">
          {intern.plan_name || 'Internship'} · {intern.weeks_submitted || 0}/{intern.weeks_total} submitted · {intern.weeks_reviewed || 0} approved · this week {currentWeek}
        </p>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3 mt-4 text-sm">
          {[
            ['Intern ID', intern.intern_code],
            ['Email', intern.email],
            ['Phone', intern.phone],
            ['College', intern.college],
            ['Date of birth', intern.date_of_birth || '—'],
            ['Start date', pretty(intern.started_at)],
            ['End date', pretty(periodEnd)],
            ['Account closes', intern.closed_at ? `Closed ${intern.closed_at}` : (intern.purge_at || 'After completion + 10 days')]
          ].map(([label, value]) => (
            <div key={label} className="rounded-2xl bg-slate-50 px-3 py-2.5">
              <p className="text-[11px] font-semibold uppercase text-gray-400">{label}</p>
              <p className="font-semibold text-gray-800 break-all">{value}</p>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          {intern.has_login && (
            <button type="button" onClick={loginAs} className="px-3 py-2 rounded-xl bg-brand-600 text-white text-sm font-semibold inline-flex items-center gap-1.5">
              <SignIn className="w-4 h-4" /> Open intern portal
            </button>
          )}
          {intern.payment_status !== 'paid' && intern.origin !== 'offline' && (
            <button type="button" onClick={() => act(() => adminInternsAPI.markPaid(intern.id))} className="px-3 py-2 rounded-xl bg-emerald-50 text-emerald-700 text-sm font-semibold">Mark paid</button>
          )}
        </div>
      </section>

      {intern.has_login && (
        <section className="rounded-2xl sm:rounded-3xl bg-white border border-gray-100 p-4 sm:p-5 shadow-soft">
          <div className="flex items-start gap-2 mb-3">
            <Key className="w-5 h-5 text-brand-600 shrink-0 mt-0.5" weight="duotone" />
            <div>
              <h2 className="font-display text-lg sm:text-xl font-bold">Change intern password</h2>
              <p className="text-sm text-gray-500">Set a new login password for {intern.intern_code}. Your admin password is required to confirm.</p>
            </div>
          </div>
          <form onSubmit={changePassword} className="grid sm:grid-cols-3 gap-2">
            {field('password', 'New intern password')}
            {field('confirm', 'Confirm new password')}
            {field('admin_password', 'Your admin password')}
            <button type="submit" disabled={busy} className="sm:col-span-3 btn-primary text-white text-sm inline-flex items-center justify-center gap-2">
              {busy ? <SpinnerGap className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              Update password
            </button>
          </form>
        </section>
      )}

      <section className="rounded-2xl sm:rounded-3xl bg-white border border-gray-100 p-4 sm:p-5 shadow-soft space-y-3">
        <div>
          <h2 className="font-display text-lg sm:text-xl font-bold">Weekly work</h2>
          <p className="text-sm text-gray-500">
            Approve any week from here — even if the intern has not submitted. It shows as approved in their portal. Reject is only for a submitted week.
          </p>
        </div>
        {tasks.length === 0 && <p className="text-sm text-gray-500">No weeks yet.</p>}
        {tasks.map((task) => {
          const done = task.status === 'submitted';
          const reviewed = task.review_status === 'reviewed';
          const expanded = openWeek === task.id;
          const isCurrent = Number(task.week_number) === currentWeek;
          const isLast = Number(task.week_number) === Number(lastWeekNo);
          const brief = briefs[task.id] || { title: task.title || '', description: task.description || '' };
          return (
            <article key={task.id} className={`rounded-2xl border ${reviewed ? 'border-emerald-100 bg-emerald-50/40' : done ? 'border-amber-100 bg-amber-50/30' : isCurrent ? 'border-brand-200 bg-brand-50/40' : 'border-gray-100 bg-slate-50'}`}>
              <div className="px-3 sm:px-4 py-3 flex flex-col gap-3">
                <button
                  type="button"
                  onClick={() => setOpenWeek(expanded ? null : task.id)}
                  className="w-full flex flex-wrap justify-between gap-2 text-left"
                >
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-900">
                      Week {task.week_number} — {task.title}
                      {isCurrent && <span className="ml-2 text-[10px] font-bold uppercase tracking-wide text-brand-700 bg-white px-2 py-0.5 rounded-full">This week</span>}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {done ? `Submitted ${task.submitted_at ? pretty(String(task.submitted_at).slice(0, 10)) : ''}` : 'Not submitted'}
                      {reviewed ? ' · Approved' : done ? ' · Waiting for approve' : ' · Pending'}
                      {` · ${task.file_count || 0} files`}
                    </p>
                  </div>
                  {reviewed ? (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
                      <CheckCircle className="w-4 h-4" weight="fill" /> Approved
                    </span>
                  ) : done ? (
                    <span className="text-xs font-semibold text-amber-700">Submitted</span>
                  ) : (
                    <span className="text-xs font-semibold text-gray-400">Pending</span>
                  )}
                </button>
                <div className="flex flex-wrap gap-2">
                  {!reviewed && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => act(() => adminInternsAPI.reviewTask(intern.id, task.id), `Week ${task.week_number} approved.`)}
                      className="text-xs font-semibold px-3 py-1.5 rounded-full bg-brand-600 text-white"
                    >
                      Approve
                    </button>
                  )}
                  {done && !(isLast && completion) && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => act(() => adminInternsAPI.rejectTask(intern.id, task.id), `Week ${task.week_number} sent back to pending.`)}
                      className="text-xs font-semibold px-3 py-1.5 rounded-full bg-amber-50 text-amber-800 border border-amber-100"
                    >
                      Reject to pending
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setOpenWeek(expanded ? null : task.id)}
                    className="text-xs font-semibold px-3 py-1.5 rounded-full bg-white border border-gray-200 text-gray-600"
                  >
                    {expanded ? 'Hide details' : 'Details'}
                  </button>
                </div>
              </div>
              {expanded && (
                <div className="px-3 sm:px-4 pb-4 space-y-3">
                  {!done && (
                    <div className="rounded-xl bg-white border border-gray-100 p-3 space-y-2">
                      <p className="text-xs font-bold uppercase text-gray-400">Set week question</p>
                      <input
                        className="input-field"
                        placeholder="Task title"
                        value={brief.title}
                        onChange={(e) => setBriefs((p) => ({ ...p, [task.id]: { ...brief, title: e.target.value } }))}
                      />
                      <textarea
                        className="input-field"
                        rows={3}
                        placeholder="Task details"
                        value={brief.description}
                        onChange={(e) => setBriefs((p) => ({ ...p, [task.id]: { ...brief, description: e.target.value } }))}
                      />
                      <button
                        type="button"
                        disabled={busy || !brief.title.trim() || !brief.description.trim()}
                        onClick={() => act(() => adminInternsAPI.setTask(intern.id, {
                          week_number: Number(task.week_number),
                          title: brief.title,
                          description: brief.description
                        }), `Week ${task.week_number} question saved.`)}
                        className="px-3 py-2 rounded-xl bg-slate-900 text-white text-xs font-semibold"
                      >
                        Save question
                      </button>
                    </div>
                  )}
                  {done && (
                    <>
                      {task.submission_text && <p className="text-sm text-gray-700 whitespace-pre-wrap">{task.submission_text}</p>}
                      {task.repo_url && (
                        <a href={task.repo_url} target="_blank" rel="noreferrer" className="text-xs font-semibold text-brand-600 inline-block">GitHub / repo</a>
                      )}
                      <div className="flex flex-wrap gap-2">
                        {task.zip_url && <a href={task.zip_url} target="_blank" rel="noreferrer" className="text-xs font-semibold text-brand-600">Download ZIP</a>}
                        {(task.screenshot_urls || []).map((url) => (
                          <a key={url} href={url} target="_blank" rel="noreferrer" className="text-xs font-semibold text-brand-600">Output screenshot</a>
                        ))}
                        {!(task.zip_url || (task.screenshot_urls || []).length) && (task.submission_urls || []).map((url) => (
                          <a key={url} href={url} target="_blank" rel="noreferrer" className="text-xs font-semibold text-brand-600">Open file</a>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </article>
          );
        })}
      </section>

      {allApproved ? (
        <section className="rounded-2xl sm:rounded-3xl bg-white border border-gray-100 p-4 sm:p-5 shadow-soft space-y-4">
          <div>
            <p className="text-xs font-bold uppercase text-brand-600 inline-flex items-center gap-1.5">
              <QrCode className="w-4 h-4" /> Final
            </p>
            <h2 className="font-display text-lg sm:text-xl font-bold">QR review then issue certificate</h2>
            <p className="text-sm text-gray-500 mt-1">
              All {lastWeekNo} weeks are approved. Check the scanned details, then issue the completion letter.
              Internship period stays {pretty(intern.started_at)} to {pretty(periodEnd)}.
            </p>
          </div>

          <div className="rounded-2xl border border-gray-100 bg-slate-50 p-3 sm:p-4 space-y-3">
            <p className="text-xs font-bold uppercase text-brand-600">Scanner review</p>
            <div className="grid sm:grid-cols-2 gap-2 text-sm">
              {[
                ['name', 'Name'],
                ['college', 'College'],
                ['intern_code', 'Intern ID'],
                ['role_title', 'Role'],
                ['phone', 'Mobile number'],
                ['email', 'Email'],
                ['join_date', 'Start date'],
                ['complete_date', 'End date'],
                ['issued_at', 'Issue date']
              ].map(([key, label]) => {
                const locked = ['intern_code', 'role_title', 'join_date', 'complete_date'].includes(key);
                return (
                  <label key={key} className="rounded-xl bg-white px-3 py-2">
                    <p className="text-[11px] font-semibold uppercase text-gray-400">{label}</p>
                    {locked ? (
                      <p className="font-semibold text-gray-800 break-all">{key.includes('date') ? pretty(scan[key]) : (scan[key] || '—')}</p>
                    ) : (
                      <input
                        className="input-field mt-1 py-2"
                        type={key === 'issued_at' ? 'date' : 'text'}
                        value={scan[key] || ''}
                        onChange={(e) => setScan((p) => ({ ...p, [key]: e.target.value }))}
                      />
                    )}
                  </label>
                );
              })}
            </div>
            <button
              type="button"
              disabled={busy}
              onClick={issueCompletion}
              className="btn-primary text-white text-sm w-full sm:w-auto"
            >
              {completion ? 'Next — update certificate' : 'Next — issue certificate'}
            </button>
          </div>

          <div className="space-y-2 text-sm text-gray-500">
            {offer && (
              <p className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span>
                  Offer letter:{' '}
                  <Link className="font-semibold text-brand-600" to={`/verify/${offer.letter_code}`} target="_blank">{offer.letter_code}</Link>
                </span>
                <Link className="font-semibold text-slate-800" to={`/verify/${offer.letter_code}?unsigned=1`} target="_blank">
                  Download without stamp/sign
                </Link>
              </p>
            )}
            {completion && (
              <p className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span>
                  Completion letter:{' '}
                  <Link className="font-semibold text-brand-600" to={`/verify/${completion.letter_code}`} target="_blank">{completion.letter_code}</Link>
                </span>
                <Link className="font-semibold text-slate-800" to={`/verify/${completion.letter_code}?unsigned=1`} target="_blank">
                  Download without stamp/sign
                </Link>
              </p>
            )}
          </div>
        </section>
      ) : (
        <section className="rounded-2xl sm:rounded-3xl bg-slate-50 border border-dashed border-gray-200 p-4 sm:p-5">
          <p className="text-xs font-bold uppercase text-gray-400">Final</p>
          <p className="text-sm text-gray-600 mt-1">
            QR review and certificate issue open after every week is approved. {intern.weeks_reviewed || 0}/{lastWeekNo} approved.
            {last ? ` Last week is ${last.review_status === 'reviewed' ? 'approved' : last.status === 'submitted' ? 'submitted' : 'pending'}.` : ''}
          </p>
          {(offer || completion) && (
            <div className="mt-3 space-y-2 text-sm text-gray-500">
              {offer && (
                <p className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span>Offer letter: <Link className="font-semibold text-brand-600" to={`/verify/${offer.letter_code}`} target="_blank">{offer.letter_code}</Link></span>
                  <Link className="font-semibold text-slate-800" to={`/verify/${offer.letter_code}?unsigned=1`} target="_blank">Download without stamp/sign</Link>
                </p>
              )}
              {completion && (
                <p className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span>Completion letter: <Link className="font-semibold text-brand-600" to={`/verify/${completion.letter_code}`} target="_blank">{completion.letter_code}</Link></span>
                  <Link className="font-semibold text-slate-800" to={`/verify/${completion.letter_code}?unsigned=1`} target="_blank">Download without stamp/sign</Link>
                </p>
              )}
            </div>
          )}
        </section>
      )}
    </div>
  );
};

export default AdminInternDetail;
